import BaseApi, { API_BASE_URL, ApiResponse } from './base';

export interface FileUploadResponse {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  createdAt: string;
}

export interface FileMetadata {
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
  provider: string;
  isMediaLibrary?: boolean;
  createdAt: string;
}

export interface PresignedUrlRequest {
  objectKey: string;
  httpMethod?: 'PUT' | 'POST';
  contentType?: string;
  expiresIn?: number;
}

export interface PresignedUrlResponse {
  presignedUrl: string;
  expiresIn: number;
  objectKey: string;
  fileUrl: string;
}

export interface SaveFileMetadataRequest {
  name: string;
  url: string;
  size: number;
  type: string;
  provider: 's3' | 'external' | 'youtube';
  isMediaLibrary?: boolean;
}

export interface UpdateFileMetadataRequest {
  name?: string;
  url?: string;
  size?: number;
  type?: string;
  isMediaLibrary?: boolean;
}

export interface QueryFilesParams {
  cursor?: string;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  provider?: string;
  fileType?: string; // MIME type filter (e.g., 'image/*', 'video/*', 'application/pdf')
  isMediaLibrary?: boolean;
}

class FilesApi extends BaseApi {
  constructor(baseURL: string = API_BASE_URL) {
    super(baseURL);
  }

  async uploadFile(file: File): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.post<FileUploadResponse>('/files/upload', formData);

    return response.data!;
  }

  async getPresignedUrl(request: PresignedUrlRequest): Promise<PresignedUrlResponse> {
    const response = await this.post<PresignedUrlResponse>('/files/presigned-url', request);
    return response.data!;
  }

  async saveFileMetadata(metadata: SaveFileMetadataRequest): Promise<FileMetadata> {
    const response = await this.post<FileMetadata>('/files/save-metadata', metadata);
    return response.data!;
  }

  async updateFileMetadata(id: string, metadata: UpdateFileMetadataRequest): Promise<FileMetadata> {
    const response = await this.patch<FileMetadata>(`/files/${id}`, metadata);
    return response.data!;
  }

  async getFiles(params?: QueryFilesParams): Promise<ApiResponse<FileMetadata[]>> {
    return this.get<FileMetadata[]>('/files', {
      params: params || {},
    });
  }
}

export default new FilesApi();
